import type { ReviewStateProps } from '@backstage/plugin-scaffolder-react/alpha';

type Schema = Record<string, any>;
type JsonObject = ReviewStateProps['formState'];
type JsonValue = JsonObject[string];

const matches = (trigger: Schema | undefined, value: JsonValue) => {
  if (Array.isArray(trigger?.enum)) return trigger!.enum.includes(value);
  if (trigger && 'const' in trigger) return trigger.const === value;
  return true;
};

/**
 * The step schema as the form renders it for these values: the branch of each
 * `dependencies` entry that the current value selects is folded into
 * `properties`. Only the `oneOf` and plain schema-dependency forms the IDP
 * templates use are resolved.
 */
export function resolveStepSchema(schema: Schema, formState: JsonObject) {
  const properties: Schema = { ...schema.properties };
  for (const [key, dependency] of Object.entries<Schema>(
    schema.dependencies ?? {},
  )) {
    const value = formState[key];
    if (value === undefined || Array.isArray(dependency)) continue;
    const branch = Array.isArray(dependency.oneOf)
      ? dependency.oneOf.find((option: Schema) =>
          matches(option.properties?.[key], value),
        )
      : dependency;
    for (const [name, property] of Object.entries<Schema>(
      branch?.properties ?? {},
    ))
      if (name !== key) properties[name] = property;
  }
  const { dependencies: _resolved, ...rest } = schema;
  return { ...rest, properties };
}

// A policy definition is reviewed on its own step; the summary only says what it holds.
function summarise(value: JsonValue | undefined): JsonValue | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const definition = value.definition as JsonObject | undefined;
  if (!definition || !Array.isArray(definition.targetKinds)) return value;
  const count = (items: JsonValue | undefined, noun: string) =>
    Array.isArray(items) && items.length
      ? [`${items.length} ${noun}${items.length === 1 ? '' : 's'}`]
      : [];
  const parts = [
    ...count(definition.rules, 'CEL rule'),
    ...count(definition.quotas, 'quota'),
  ];
  return {
    ...value,
    definition: `${
      parts.join(', ') || 'empty'
    } for ${definition.targetKinds.join(', ')}`,
  };
}

/**
 * Review shows only what this submission is about. RJSF keeps the values of a
 * branch the user left (Backstage ignores omitExtraData/liveOmit), so without
 * this a field from another operation is listed under its raw key (#15878).
 */
export function activeReview({
  formState,
  schemas,
}: ReviewStateProps): ReviewStateProps {
  const resolved = schemas.map(step => ({
    ...step,
    mergedSchema: resolveStepSchema(step.mergedSchema, formState),
  }));
  const active = new Set(
    resolved.flatMap(step => Object.keys(step.mergedSchema.properties ?? {})),
  );
  return {
    schemas: resolved,
    formState: Object.fromEntries(
      Object.entries(formState)
        .filter(([key]) => active.has(key))
        .map(([key, value]) => [key, summarise(value) as JsonValue]),
    ),
  };
}
